import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SingleSpouseType, SpouseType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Spouse } from 'service/dto/spouse.dto';

export const useCreateSpouse = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (spouse: SpouseType): Promise<void> => {
      return services.spouseService.createSpouse(spouse);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-spouse']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateSpouse = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      spouseId,
      spouse
    }: {
      spouseId: number | null;
      spouse: SingleSpouseType;
    }): Promise<void> => {
      return services.spouseService.updateSpouse(spouse, spouseId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-spouse']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetSpousesList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['spouses'],
    () => services.spouseService.getSpouses(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteSpouses = () =>
  useMutation(
    (spouseId: number) => services.spouseService.deleteSpouse(spouseId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetSpousDetails = ({ spouseId }: { spouseId: number }) => {
  return useQuery<Spouse, AxiosError<Error>>(
    ['get_spouse', spouseId],
    () => services.spouseService.getSpouse(spouseId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!spouseId
    }
  );
};
